import { useCallback, useEffect } from 'react';

/**
 * Fires `getData` whenever `target` scrolls into its root box.
 *
 * Takes the element, not a ref: callers hold it in state (`ref={setTarget}`), so the observer follows
 * the element's mount. A loader row inside a portal mounts in the portal's own render, after the
 * owner's last effect has run; an effect keyed on a ref object would never see it.
 */
export const useInfiniteScroll = (
    target: HTMLElement | null,
    options: {
        observer: IntersectionObserverInit;
        disabled?: boolean;
        isLoading?: boolean;
        /** Called when the target LEAVES the root box. The intersecting edge alone cannot tell a
         *  caller that the user moved away again, which is what lets a one-request-per-approach
         *  latch re-arm. Optional — callers that only care about arrival ignore it. */
        onLeave?: () => void;
    },
    getData: () => void
) => {
    const { onLeave } = options;

    const handleIntersection = useCallback(
        (entries: IntersectionObserverEntry[]) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) {
                    onLeave?.();
                    return;
                }
                if (!options.disabled && !options.isLoading) {
                    getData();
                }
            });
        },
        [getData, onLeave, options.disabled, options.isLoading]
    );

    useEffect(() => {
        if (!target) return;
        const observer = new IntersectionObserver(handleIntersection, options.observer);
        observer.observe(target);
        return () => observer.disconnect();
    }, [target, handleIntersection, options]);
};
